'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import AppLogo from '@/components/ui/AppLogo';
import { Target, Layers, FileText, CheckSquare } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


export default function AuthScreen() {
  const [tab, setTab] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[560px] flex-col justify-between bg-card border-r border-border p-10 flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <AppLogo size={36} />
          <span className="font-semibold text-lg tracking-tight text-foreground">MissionLog</span>
        </div>

        <div className="space-y-8">
          <div>
            <h2 className="text-3xl font-bold text-foreground leading-tight">
              Your day, structured.<br />
              <span className="text-primary">Mission by mission.</span>
            </h2>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              MissionLog gives every task a home — with subtasks, inline output workspaces, and status tags that survive task completion.
            </p>
          </div>

          {/* Feature list */}
          <div className="space-y-4">
            {[
              {
                key: 'feat-missions',
                icon: Target,
                title: 'Daily Missions',
                desc: 'Overarching goals that frame your entire day',
              },
              {
                key: 'feat-tasks',
                icon: CheckSquare,
                title: 'Hierarchical Tasks',
                desc: 'Tasks with subtask breakdowns and progress tracking',
              },
              {
                key: 'feat-output',
                icon: FileText,
                title: 'Inline Output Workspaces',
                desc: 'Drafts, notes, and code live inside the task that created them',
              },
              {
                key: 'feat-status',
                icon: Layers,
                title: 'Persistent Status Tags',
                desc: 'Needs Approval and Follow-Up tags persist even after completion',
              },
            ].map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.key} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon size={15} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{f.title}</p>
                    <p className="text-xs text-muted-foreground">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <p className="text-[10px] text-muted-foreground">
            © 2026 MissionLog · Privacy · Terms
          </p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="font-semibold text-base tracking-tight text-foreground">MissionLog</span>
          </div>

          {/* Tab switcher */}
          <div className="flex rounded-lg bg-muted p-1 mb-8">
            {(['login', 'signup'] as const).map((t) => (
              <button
                key={`tab-${t}`}
                onClick={() => setTab(t)}
                className={`flex-1 text-sm font-medium py-2 rounded-md transition-all duration-200
                  ${tab === t ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {t === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {tab === 'login' ? (
            <LoginForm onSwitchToSignup={() => setTab('signup')} />
          ) : (
            <SignupForm onSwitchToLogin={() => setTab('login')} />
          )}
        </div>
      </div>
    </div>
  );
}